import {
  FirstPage,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  LastPage,
  Pause,
  PlayArrow,
} from '@mui/icons-material';
import {
  Alert,
  Backdrop,
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
} from '@mui/material';
import dayjs from 'dayjs';
import {t} from 'i18next';
import update from 'immutability-helper';
import L from 'leaflet';
import {useEffect, useMemo, useState} from 'react';
import {
  CircleMarker,
  MapContainer,
  Marker,
  Polyline,
  TileLayer,
  Tooltip,
  useMap,
} from 'react-leaflet';

import API, {getMessagesFromApiError} from '../../../api/axios';
import {apiBaseUrl} from '../../../api/urls';
import {
  DashboardEntity,
  DashboardPanelData,
} from '../../../interfaces/Dashboard';
import {COLORED_LEAFLET_ICONS} from '../../../utils/icons';
import {
  usePanel,
  usePanelFilter,
} from '../../dashboards/entities/DashboardEntityContext';
import {DateTimeSelect} from '../../selectors/DateTimeSelect';
import {SafeyeNodeSelector} from '../../selectors/SafeyeNodeSelector';
import {DashboardPanelTitleSlot} from '../DashboardPanelTitleSlot';
import HazardAIMachineLocationHistoryExportExcel from './HazardAIMachineLocationHistoryExportExcel';

const MapController = ({
  position,
  zoom,
  fetchedData,
}: {
  position: [number, number];
  zoom?: number;
  fetchedData?: any[];
}) => {
  const map = useMap();

  useEffect(() => {
    map.setView(position, zoom ?? map.getZoom());
  }, [position, zoom, map]);

  useEffect(() => {
    if (fetchedData && fetchedData.length) {
      const bounds = L.latLngBounds(
        fetchedData.map((p: any) => [p.latitude, p.longitude])
      );
      map.fitBounds(bounds);
    }
  }, [fetchedData, map]);

  return null;
};

interface Props {
  value?: DashboardPanelData;
  entities?: DashboardEntity[];
}

export const HazardAIMachineLocationHistory = ({value, entities}: Props) => {
  const [panel] = usePanel();
  const {filter, setFilter} = usePanelFilter();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [speed] = useState(1);
  //const [speed, setSpeed] = useState(10);
  const [initialPosition, setInitialPosition] = useState<[number, number]>([
    0, 0,
  ]);

  const [selectedMachineId, setSelectedMachineId] = useState(
    value?.['hazard_ai_machine_location'].selectedMachineId || null
  );

  useEffect(() => {
    const selMacID = value?.['hazard_ai_machine_location']?.selectedMachineId;
    if (selMacID && selMacID !== selectedMachineId) {
      setSelectedMachineId(selMacID);
    }
  }, [value?.['hazard_ai_machine_location']?.selectedMachineId, entities]);

  const [fetchedData, setFetchedData] = useState<any[]>([]);
  const [fetchedErrors, setFetchedErrors] = useState<string[]>([]);
  const [fetchedInProgress, setFetchedInProgress] = useState(false);

  const params = useMemo(() => {
    return {
      date_start:
        filter?.params?.date_start ||
        dayjs().subtract(30, 'minutes').format('YYYY-MM-DD HH:mm:ss'),
      date_end:
        filter?.params?.date_end || dayjs().format('YYYY-MM-DD HH:mm:ss'),
      object: filter?.params?.object || 'all',
    };
  }, [filter]);

  const fetchData = async (params: any) => {
    if (!selectedMachineId) {
      return;
    }

    setFetchedInProgress(true);
    setFetchedErrors([]);
    try {
      const endpoint = `${apiBaseUrl}/hazard-ai/${selectedMachineId}/machine-location-history`;
      const resp = await API.get(endpoint, {
        params,
      });
      setFetchedData(resp.data || []);
      if (resp.data.length) {
        setInitialPosition([resp.data[0].latitude, resp.data[0].longitude]);
        // setting currentIndex to length of the array so that slice will
        // return the entire array when calculating traveledPath below
        setCurrentIndex(resp.data.length);
      }
    } catch (error: any) {
      const messages = getMessagesFromApiError(error, 'STRA_d0a60f7b');
      setFetchedErrors(messages);
    }
    setFetchedInProgress(false);
  };

  useEffect(() => {
    if (params) {
      fetchData(params);
    }
  }, [params, selectedMachineId]);

  const handlePlay = () => {
    if (fetchedData.length > 1) {
      if (!isPaused || currentIndex >= fetchedData.length) {
        setCurrentIndex(0);
      }
      setIsPaused(false);
      setIsPlaying(true);
    }
  };

  const handlePause = () => {
    setIsPaused(!isPaused);
  };

  useEffect(() => {
    if (isPaused) {
      return;
    }

    if (!isPlaying || currentIndex >= fetchedData.length) {
      setIsPlaying(false);
      return;
    }

    //const now = fetchedData[currentIndex]?.timestamp;
    const next = fetchedData[currentIndex + 1]?.timestamp;

    if (!next) {
      setIsPlaying(false);
      setCurrentIndex((prev) => prev + 1);
      return;
    }

    // todo use a setting to determine speed of playback rather than hard coding the value in the settimeout below
    //const timeDiff = dayjs(next).diff(dayjs(now));
    //const delay = timeDiff >= 0 ? timeDiff / speed : 1000;

    const timer = setTimeout(() => {
      setCurrentIndex((prev) => prev + 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [isPlaying, isPaused, currentIndex, fetchedData, speed]);

  const traveledPath = fetchedData?.slice(0, currentIndex + 1);
  const currentPoint = traveledPath?.[traveledPath.length - 1 || 0] || {
    latitude: 0,
    longitude: 0,
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      height="calc(100% - 38px)"
      width="100%"
      overflow="hidden"
    >
      <Backdrop open={fetchedInProgress} sx={{position: 'absolute'}}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <DashboardPanelTitleSlot>
        {t(`panels.${panel?.code}`)}
      </DashboardPanelTitleSlot>

      {/* Filters */}
      <Box display="flex" gap={1} py={1}>
        <Box maxWidth={400} minWidth={200}>
          <SafeyeNodeSelector
            size="small"
            fullWidth
            multiple={false}
            allOptionDisabled
            value={selectedMachineId}
            valueProperty="machine_id"
            onChange={(v) => {
              setSelectedMachineId(v);
            }}
          />
        </Box>
        {/* <Box maxWidth={400} minWidth={200}>
          <TextField
            select
            fullWidth
            id="object-select"
            value={params.object}
            label="Object"
            size="small"
            onChange={(e) => {
              setFilter(
                update(filter, {
                  params: {
                    object: {
                      $set: String(e.target.value),
                    },
                  },
                })
              );
            }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="person">People</MenuItem>
            <MenuItem value="vehicle">Machine</MenuItem>
          </TextField>
        </Box> */}
        <Box maxWidth={400}>
          <DateTimeSelect
            value={dayjs(params.date_start).toDate()}
            label="Start (30 minute playback)"
            renderInput={{
              size: 'small',
            }}
            onChange={(v) => {
              setFilter(
                update(filter, {
                  params: {
                    date_start: {
                      $set: dayjs(v).format('YYYY-MM-DD HH:mm:ss'),
                    },
                    date_end: {
                      $set: dayjs(v)
                        .add(30, 'minutes')
                        .format('YYYY-MM-DD HH:mm:ss'),
                    },
                  },
                })
              );
            }}
          />
        </Box>
        {selectedMachineId ? (
          <Box>
            <HazardAIMachineLocationHistoryExportExcel
              id={selectedMachineId}
            ></HazardAIMachineLocationHistoryExportExcel>
          </Box>
        ) : (
          <></>
        )}
        {fetchedData?.length ? (
          <Box>
            {/* <Select
            labelId="playback-speed-select-label"
            id="playback-speed-select"
            value={speed}
            label="Playback Speed"
            size="small"
            onChange={(e) => {
              setSpeed(Number(e.target.value));
              }}
              >
              <MenuItem value={1}>1x</MenuItem>
              <MenuItem value={10}>10x</MenuItem>
              <MenuItem value={50}>50x</MenuItem>
              <MenuItem value={100}>100x</MenuItem>
              </Select> */}
            <ButtonGroup>
              {isPlaying ? (
                <>
                  <Button
                    disabled={currentIndex <= 0}
                    onClick={() => {
                      setCurrentIndex(0);
                    }}
                  >
                    <FirstPage />
                  </Button>
                  <Button
                    disabled={currentIndex <= 0}
                    onClick={() => {
                      setCurrentIndex(currentIndex - 1);
                    }}
                  >
                    <KeyboardArrowLeft />
                  </Button>
                </>
              ) : (
                <></>
              )}

              {isPlaying && !isPaused ? (
                <Button onClick={handlePause}>
                  <Pause /> {currentIndex + 1}/{fetchedData.length}
                </Button>
              ) : (
                <Button onClick={handlePlay}>
                  <PlayArrow /> {isPaused ? currentIndex + 1 : currentIndex}/
                  {fetchedData.length}
                </Button>
              )}

              {isPlaying ? (
                <>
                  <Button
                    disabled={currentIndex >= fetchedData.length - 1}
                    onClick={() => {
                      setCurrentIndex(currentIndex + 1);
                    }}
                  >
                    <KeyboardArrowRight />
                  </Button>
                  <Button
                    disabled={currentIndex >= fetchedData.length - 1}
                    onClick={() => {
                      setCurrentIndex(fetchedData.length - 1);
                    }}
                  >
                    <LastPage />
                  </Button>
                </>
              ) : (
                <></>
              )}
              {/* <Button onClick={handleReset}>
                <Restore /> Reset
              </Button> */}
            </ButtonGroup>
          </Box>
        ) : (
          <></>
        )}
      </Box>

      {/* Errors */}
      {fetchedErrors.map((error, idx) => (
        <Alert
          key={idx}
          severity="error"
          onClose={() => params && fetchData(params)}
        >
          {error}
        </Alert>
      ))}

      <MapContainer
        //ref={mapRef}
        center={initialPosition}
        zoom={2}
        style={{height: '90vh', width: '100%'}}
        // whenReady={() => {
        //   const bounds = L.latLngBounds(fetchedData.map((p) => [p.lat, p.lon]));
        //   //mapRef.fitBounds(bounds);
        // }}
      >
        <MapController
          position={initialPosition}
          fetchedData={fetchedData}
        ></MapController>
        <TileLayer
          attribution="&copy; OpenStreetMap contributors"
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {/* Animated growing path */}
        <Polyline
          positions={traveledPath.map((p) => [p.latitude, p.longitude])}
          color="blue"
        />
        {/* Moving marker */}
        {currentPoint.latitude && currentPoint.longitude ? (
          <Marker
            position={[currentPoint.latitude, currentPoint.longitude]}
            icon={COLORED_LEAFLET_ICONS.assetEvents['default']}
          />
        ) : (
          <></>
        )}
        {/* Red X markers */}
        {traveledPath.map((i) =>
          i.type === 'heartbeat' ? (
            // <CircleMarker
            //   key={`x-${i.id}`}
            //   center={i?.latitude ? [i?.latitude, i?.longitude] : [0, 0]}
            //   radius={0}
            //   pathOptions={{color: 'black'}}
            // >
            //   <Tooltip direction="top" offset={[0, -34]} pane="popupPane">
            //     <Box display="flex" flexDirection="column" gap={0.5}>
            //       <Box>Timestamp: {i?.timestamp}</Box>
            //     </Box>
            //   </Tooltip>
            // </CircleMarker>
            <></>
          ) : (
            <CircleMarker
              key={`x-${i.id}`}
              center={i?.latitude ? [i?.latitude, i?.longitude] : [0, 0]}
              radius={5}
              pathOptions={{color: 'red'}}
            >
              <Tooltip direction="top" offset={[0, -34]} pane="popupPane">
                <Box display="flex" flexDirection="column" gap={0.5}>
                  <Box textTransform="capitalize">
                    Object: {i?.object?.replace('_', ' ')}
                  </Box>
                  <Box textTransform="capitalize">Zone: {i?.zone}</Box>
                  <Box>Timestamp: {i?.timestamp}</Box>
                </Box>
              </Tooltip>
            </CircleMarker>
          )
        )}
      </MapContainer>
    </Box>
  );
};
